import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function JoinPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("code") || "").replace(/\D/g, "").slice(0, 6));
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState("");

  // Auto-lookup preview when 6 digits provided.
  useEffect(() => {
    if (code.length !== 6) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      setError("");
      try {
        const { data } = await api.post("/cases/lookup-invite", { code });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setError(extractError(e, "Could not look up invite"));
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const handleJoin = async () => {
    if (code.length !== 6) return;
    if (!user) {
      // Send through registration; preserve the code so they return here after.
      navigate(`/register?next=${encodeURIComponent(`/join?code=${code}`)}`, { replace: false });
      return;
    }
    setJoining(true);
    setError("");
    try {
      const { data } = await api.post("/cases/join", { code });
      navigate(`/cases/${data.id}`, { replace: true });
    } catch (e) {
      setError(extractError(e, "Could not join case"));
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12 fade-in" data-testid="join-page">
      <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--participant-b)] font-medium">
        You've been invited
      </p>
      <h1 className="font-serif text-3xl md:text-4xl mt-1 mb-3">Join a FairResolver case</h1>
      <p className="text-sm text-[color:var(--text-muted)] mb-5">
        Enter the 6-digit invite code from your email. You'll need an account
        to participate — we'll guide you through one if you don't have it yet.
      </p>

      <label className="text-sm text-[color:var(--text-muted)] block mb-1">Invite code</label>
      <input
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="123456"
        className="w-full px-4 py-3 rounded-xl border border-[color:var(--line)] bg-white outline-none focus:ring-2 focus:ring-[color:var(--participant-b)]/30 text-2xl tracking-[0.4em] font-serif text-center"
        data-testid="join-code-input"
      />

      {previewLoading && (
        <div className="mt-3 text-xs text-[color:var(--text-muted)]" data-testid="join-preview-loading">
          Looking up code…
        </div>
      )}

      {preview && (
        <div
          className="mt-4 rounded-xl bg-[color:var(--surface-sage)] border border-[color:var(--line)] p-4"
          data-testid="join-preview"
        >
          <div className="text-xs uppercase tracking-wider text-[color:var(--text-muted)]">Case</div>
          <div className="font-serif text-xl mt-1">{preview.title}</div>
          <div className="text-sm text-[color:var(--text-muted)] mt-1">
            Started by <b className="text-[color:var(--text-primary)]">{preview.participant_a_name}</b> · {preview.tier} tier
          </div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">
            Sent to {preview.participant_b_email}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 text-sm text-[color:var(--counter-error)]" data-testid="join-error">
          {error}
        </div>
      )}

      <button
        type="button"
        disabled={code.length !== 6 || joining || authLoading || (code.length === 6 && !preview && !previewLoading) }
        onClick={handleJoin}
        className="mt-5 w-full px-4 py-3 rounded-full bg-[color:var(--participant-b)] text-white font-semibold hover:opacity-90 transition disabled:opacity-50"
        data-testid="join-submit"
      >
        {joining ? "Joining…" : user ? "Join this case" : "Continue to sign in / create account"}
      </button>

      {!user && (
        <p className="text-xs text-[color:var(--text-muted)] mt-3 text-center">
          Already have an account?{" "}
          <a
            href={`/login?next=${encodeURIComponent(`/join?code=${code}`)}`}
            className="underline text-[color:var(--participant-b)]"
            data-testid="join-login-link"
          >
            Sign in instead
          </a>
        </p>
      )}
    </div>
  );
}
